import React, { useRef } from 'react';
import { Container } from "./Entity.styles";
import {
  Entity as IssuesEntity,
  isEndpointEntity as isIssuesEndpointEntity,
  isFunctionEntity as isIssuesFunctionEntity,
  isKafkaTopicEntity as isIssuesKafkaTopicEntity
} from '@hud/portal-dtos';

import {
  EntitySchema as OverviewEntity,
  EndpointEntitySchema,
  FunctionEntitySchema,
  QueueEntitySchema
} from '@hud/portal-dtos/webapp-overview.dto';

import { HudIcon, IconType } from '../../../Icons/HudIcons';
import Tooltip from '../Tooltip/Tooltip';

type EntityProps = {
  entity: IssuesEntity | OverviewEntity
  entityUrl?: string;
};

function isIssuesEntity(entity: IssuesEntity | OverviewEntity): entity is IssuesEntity {
  return 'issues' in entity;
}

// Type guards for each entity type
function isEndpointEntity(entity: OverviewEntity): entity is EndpointEntitySchema {
  return (entity as EndpointEntitySchema).path !== undefined;
}
function isFunctionEntity(entity: OverviewEntity): entity is FunctionEntitySchema {
  return (entity as FunctionEntitySchema).signature !== undefined;
}
function isQueueEntity(entity: OverviewEntity): entity is QueueEntitySchema {
  return (entity as QueueEntitySchema).queueName !== undefined;
}

type EntityDetails = {
  icon: string;
  name: string;
}

// Helper function to get entity details
function getEntityDetails(entity: IssuesEntity | OverviewEntity): EntityDetails {
  if (isIssuesEntity(entity)) {
    if (isIssuesEndpointEntity(entity)) {
      return { icon: 'designSystem.endpoint', name: entity.path };
    }
    if (isIssuesFunctionEntity(entity)) {
      return { icon: 'designSystem.function', name: entity.name };
    }
    if (isIssuesKafkaTopicEntity(entity)) {
      return { icon: 'designSystem.queue', name: entity.name };
    }
    return { icon: '', name: '' };
  }

  // entity is OverviewEntity (EntitySchema)
  if (isEndpointEntity(entity)) {
    return { icon: 'designSystem.endpoint', name: entity.path };
  }
  if (isFunctionEntity(entity)) {
    return { icon: 'designSystem.function', name: entity.signature };
  }
  if (isQueueEntity(entity)) {
    return { icon: 'designSystem.queue', name: entity.queueName };
  }
  return { icon: 'designSystem.bell', name: 'Unknown' }; // this is a placeholder
}

const Entity: React.FC<EntityProps> = ({ entity, entityUrl }) => {
  const entityDetails = getEntityDetails(entity);
  const ellipsisRef = useRef(null);
  return <Container>
    <HudIcon icon={entityDetails.icon as IconType} size={'20px'} />
      <a
        href={entityUrl}
        className="experimentalEllipsis"
      >
        <Tooltip label={entityDetails.name} wordwrap={false} ellipsisRef={ellipsisRef}>
          <span ref={ellipsisRef}>{entityDetails.name}</span>
        </Tooltip>
      </a>
    </Container>;
}

export default Entity;
